import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumb from '../../components/ui/Breadcrumb';
import RequiredServicesStats from './components/RequiredServicesStats';
import RequiredServicesTable from './components/RequiredServicesTable';
// import DeploymentPointModal from './components/DeploymentPointModal';
import Button from '../../components/ui/Button';
import httpHelper, {Method_TYPE} from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';

const RequiredServicesManagement = () => {
  const [deploymentPoints, setDeploymentPoints] = useState([]);
  const [deploymentDetails, setDeploymentDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Mock deployment points data
  const mockDeploymentPoints = [
    {
      id: 'DP001',
      deploymentPointName: 'Main Registration Desk',
      description: 'Primary registration point for all devotees entering the premises',
      sector: 'registration',
      requiredCount: 15,
      allocatedCount: 12,
      leader1: 'Rajesh Kumar',
      leader2: 'Priya Sharma',
      skillsRequired: ['Communication', 'Computer Skills', 'Language Skills']
    },
    {
      id: 'DP002',
      deploymentPointName: 'Kitchen Service - Breakfast',
      description: 'Preparation and serving of morning prasadam',
      sector: 'kitchen',
      requiredCount: 25,
      allocatedCount: 20,
      leader1: 'Suresh Babu',
      leader2: 'Lakshmi Devi',
      skillsRequired: ['Cooking', 'Food Safety']
    },
    {
      id: 'DP003',
      deploymentPointName: 'Security - Main Gate',
      description: 'Security monitoring and crowd control at main entrance',
      sector: 'security',
      requiredCount: 8,
      allocatedCount: 3,
      leader1: 'Venkatesh Rao',
      leader2: null,
      skillsRequired: ['Leadership', 'Communication']
    },
    {
      id: 'DP004',
      deploymentPointName: 'Medical Aid Station',
      description: 'First aid and medical assistance for devotees',
      sector: 'medical',
      requiredCount: 6,
      allocatedCount: 6,
      leader1: 'Dr. Meera Nair',
      leader2: 'Nurse Kavitha',
      skillsRequired: ['First Aid', 'Medical Knowledge']
    },
    {
      id: 'DP005',
      deploymentPointName: 'Transport Coordination',
      description: 'Managing vehicle parking and transportation logistics',
      sector: 'transport',
      requiredCount: 12,
      allocatedCount: 8,
      leader1: 'Raman Pillai',
      leader2: 'Gopal Krishna',
      skillsRequired: ['Driving', 'Traffic Management']
    },
    {
      id: 'DP006',
      deploymentPointName: 'Cleaning - Dining Hall',
      description: 'Maintaining cleanliness in dining and common areas',
      sector: 'cleaning',
      requiredCount: 10,
      allocatedCount: 7,
      leader1: 'Kamala Devi',
      leader2: null,
      skillsRequired: ['Attention to Detail']
    },
    {
      id: 'DP007',
      deploymentPointName: 'Kitchen Service - Lunch',
      description: 'Preparation and serving of afternoon prasadam',
      sector: 'kitchen',
      requiredCount: 30,
      allocatedCount: 25,
      leader1: 'Anand Kumar',
      leader2: 'Radha Krishnan',
      skillsRequired: ['Cooking', 'Food Safety', 'Leadership']
    },
    {
      id: 'DP008',
      deploymentPointName: 'Registration - VIP Counter',
      description: 'Special registration desk for VIP guests and dignitaries',
      sector: 'registration',
      requiredCount: 4,
      allocatedCount: 2,
      leader1: 'Srinivas Rao',
      leader2: null,
      skillsRequired: ['Communication', 'Protocol Knowledge', 'Language Skills']
    }
  ];



  const navigate = useNavigate();

  useEffect(() => {
    const role = localStorage.getItem('role');

    if (!role) {
      navigate('/login-screen');
    }

    if(role == 'basic' || role == 'leader') {
      navigate('/dashboard');
    }
    
  }, [navigate]);

  useEffect(() => {

    const fetchData = async () => {

      try {

        const data = await httpHelper(URL_CONSTANTS.CURRENT_BATCH, Method_TYPE.GET);

        const deploymentStats = await httpHelper(`${URL_CONSTANTS.DEPLOYMENT_POINTS}/${data._id.toString()}`, Method_TYPE.GET);

        setDeploymentDetails(deploymentStats); // Update the state with the new counts

      } catch (error) {

        console.error('Error fetching data:', error);

      }
    };
    // Simulate API call to fetch deployment points
    const fetchDeploymentPoints = async () => {
      setIsLoading(true);
      try {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setDeploymentPoints(mockDeploymentPoints);
      } catch (error) {
        console.error('Error fetching deployment points:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeploymentPoints();
    fetchData();
  }, []);

  const handleExport = (format) => {
    console.log(`Exporting deployment points data in ${format} format`);
    // Implementation for export functionality would go here
  };

  const handleViewDetails = (point) => {
    setSelectedPoint(point);
    setIsModalOpen(true);
  };

  const handleSavePoint = (pointData) => {
    if (selectedPoint) {
      // Update existing point
      setDeploymentPoints(prev => 
        prev.map(point => 
          point.id === selectedPoint.id 
            ? { ...point, ...pointData }
            : point
        )
      );
    } else {
      // Add new point
      const newPoint = {
        id: `DP${String(deploymentPoints.length + 1).padStart(3, '0')}`,
        ...pointData,
        allocatedCount: 0
      };
      setDeploymentPoints(prev => [...prev, newPoint]);
    }
  };

  const handleAddNew = () => {
    setSelectedPoint(null);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background ">
        <Helmet>
          <title>Required Services Management - Tamil Nadu Prasanthi Seva</title>
        </Helmet>
        <Header />
        <Breadcrumb />
        
        <main className="pt-8 pb-8">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="animate-spiritual-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-card rounded-lg border border-border p-4">
                    <div className="h-10 bg-muted rounded mb-3"></div>
                    <div className="h-6 bg-muted rounded mb-2"></div>
                    <div className="h-4 bg-muted rounded"></div>
                  </div>
                ))}
              </div>
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="h-64 bg-muted rounded"></div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Required Services Management - Tamil Nadu Prasanthi Seva</title>
        <meta name="description" content="Manage deployment point requirements and allocation gaps across the organization" />
      </Helmet>
      
      <Header />
      <Breadcrumb />
      
      <main className="pt-8 pb-8">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-heading font-semibold text-2xl text-foreground mb-2">
                Required Services Management
              </h1>
              <p className="text-muted-foreground">
                Monitor deployment point requirements and allocation gaps across all sectors
              </p>
            </div>
            
            {/* <Button
              variant="default"
              onClick={handleAddNew}
              iconName="Plus"
              iconPosition="left"
            >
              Add Deployment Point
            </Button> */}
          </div>

          {/* Statistics Cards */}
          <RequiredServicesStats deploymentPoints={deploymentDetails} />

          {/* Main Table */}
          <RequiredServicesTable
            deploymentPoints={deploymentDetails}
            onExport={handleExport}
            onViewDetails={handleViewDetails}
          />

          {/* Modal */}
          {/* <DeploymentPointModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            deploymentPoint={selectedPoint}
            onSave={handleSavePoint}
          /> */}
        </div>
      </main>
      {/* <Footer /> */}
    </div>
  );
};

export default RequiredServicesManagement;